import {
  IconButton,
  IconButtonGroup,
  RedoIcon,
  Tooltip,
  UndoIcon,
} from '@frontend/legacy-components';

import { redoShortcutLabel, undoShortcutLabel } from '../utils/shortcutLabels';

export type DxfUndoRedoGroupProps = {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  /** Both buttons are inert while the file is locked. */
  isDisabled?: boolean;
};

/**
 * Undo and redo for mapping edits, beside the tool strips in the mapping toolbar.
 * Presentational: the session's history store owns what can be stepped.
 */
export const DxfUndoRedoGroup = ({
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  isDisabled = false,
}: DxfUndoRedoGroupProps) => {
  const isUndoDisabled = isDisabled || !canUndo;
  const isRedoDisabled = isDisabled || !canRedo;

  return (
    <IconButtonGroup
      size="sm"
      display="inline-flex"
      // IconButton's own group gap is a :not(:last-child) margin of 3px. A
      // disabled tooltip wraps the button and drops that margin, so the
      // toolbar jumps when undo or redo enables. Zero it and keep the 7px
      // design gap on the group instead.
      gap="7px"
    >
      {/* A disabled button swallows hover, so the tooltip wraps it while inert. */}
      <Tooltip
        placement="bottom"
        label={canUndo ? `Undo (${undoShortcutLabel()})` : 'Nothing to undo'}
        shouldWrapChildren={isUndoDisabled}
      >
        <IconButton
          aria-label="Undo"
          isDisabled={isUndoDisabled}
          onClick={onUndo}
          p={0}
          m={0}
          borderRadius="3px"
          _notLast={{ marginInlineEnd: 0 }}
        >
          <UndoIcon boxSize={3} color="inherit" />
        </IconButton>
      </Tooltip>
      <Tooltip
        placement="bottom"
        label={canRedo ? `Redo (${redoShortcutLabel()})` : 'Nothing to redo'}
        shouldWrapChildren={isRedoDisabled}
      >
        <IconButton
          aria-label="Redo"
          isDisabled={isRedoDisabled}
          onClick={onRedo}
          p={0}
          m={0}
          borderRadius="3px"
          _notLast={{ marginInlineEnd: 0 }}
        >
          <RedoIcon boxSize={3} color="inherit" />
        </IconButton>
      </Tooltip>
    </IconButtonGroup>
  );
};
DxfUndoRedoGroup.displayName = 'DxfUndoRedoGroup';
